import clsx from 'clsx';
import { useId } from 'react';

interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
  ariaLabel: string;
}

export const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  disabled,
  className,
  ariaLabel,
}: SegmentedControlProps<T>) => {
  const name = useId();
  return (
    <fieldset
      disabled={disabled}
      className={clsx('grid auto-cols-fr grid-flow-col rounded bg-neutral-100 p-0.5', className)}
    >
      <legend className="sr-only">{ariaLabel}</legend>
      {options.map((option) => (
        <label key={option.value} className="cursor-pointer has-disabled:cursor-not-allowed">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
            className="peer sr-only"
          />
          <span className="flex h-9 items-center justify-center rounded px-3 text-sm whitespace-nowrap text-neutral-600 duration-300 peer-checked:bg-white peer-checked:font-bold peer-checked:text-sky-800 peer-checked:shadow-sm peer-focus-visible:ring-2 peer-focus-visible:ring-sky-500 hover:text-zinc-800">
            {option.label}
          </span>
        </label>
      ))}
    </fieldset>
  );
};
